import { Layout, Typography, Empty, Spin } from "antd";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";
import dayjs from "dayjs";
import { useGetLevelBStatistics } from "@hooks";
import { LevelBReportFilter, LevelBCandidateSummary } from "../types/levelb-reports";
import { LevelBReportFilters, LevelBOverallStats, LevelBCompetencyChart, LevelBCandidateTable, LevelBCandidateDetail, LevelBExportButton, LevelBParticipantCompetencyTable } from "@components";

export const LevelBReportsPage = () => {
  const { t } = useTranslation();

  // Default to last 30 days
  const defaultFilter = useMemo<LevelBReportFilter>(() => ({
    startDate: dayjs().subtract(30, 'day').startOf('day').toISOString(),
    endDate: dayjs().endOf('day').toISOString(),
  }), []);

  const [filter, setFilter] = useState<LevelBReportFilter>(defaultFilter);

  // Candidate detail drawer state
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { 
    data: statistics, 
    isLoading, 
    refetch,
    isError 
  } = useGetLevelBStatistics(filter);

  const handleFilterChange = (newFilter: LevelBReportFilter) => {
    setFilter(newFilter);
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleCandidateClick = useCallback((candidate: LevelBCandidateSummary) => {
    setSelectedCandidateId(candidate.examInstanceId);
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setSelectedCandidateId(null);
  }, []);

  const hasData = statistics && statistics.totalCandidates > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 1 }}
    >
      <Layout>
        {/* Page Title with Export Button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ display: "flex" }}>
            <div
              style={{
                width: 3,
                backgroundColor: "#722ED1",
                borderRadius: 100,
                marginRight: 12,
              }}
            />
            <Typography.Title level={2} style={{ margin: 0 }}>
              {t("levelBReports.title")}
            </Typography.Title>
          </div>
          {hasData && (
            <LevelBExportButton filter={filter} />
          )}
        </div>

        {/* Filters */}
        <LevelBReportFilters
          filter={filter}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          isLoading={isLoading}
        />

        {/* Content */}
        {isLoading ? (
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin size="large" />
            <Typography.Text type="secondary" style={{ display: "block", marginTop: 16 }}>
              {t("levelBReports.loading")}
            </Typography.Text>
          </div>
        ) : isError ? (
          <Empty
            description={t("levelBReports.errors.loadFailed")}
            style={{ marginTop: 48 }}
          />
        ) : hasData ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Overall Statistics */}
            <LevelBOverallStats
              statistics={statistics}
              isLoading={isLoading}
            />

            {/* Competency Performance Chart */}
            <LevelBCompetencyChart
              competencies={statistics?.competencyStatistics}
              overallCompetencies={statistics?.overallCompetencyStatistics}
              isLoading={isLoading}
            />

            {/* Participant Competency Points Table */}
            <LevelBParticipantCompetencyTable filter={filter} />

            {/* Candidate Table */}
            <LevelBCandidateTable
              filter={filter}
              onCandidateClick={handleCandidateClick}
            />
          </motion.div>
        ) : (
          <Empty
            description={t("levelBReports.noData")}
            style={{ marginTop: 48 }}
          />
        )}

        {/* Candidate Detail Drawer */}
        <LevelBCandidateDetail
          examInstanceId={selectedCandidateId}
          open={drawerOpen}
          onClose={handleDrawerClose}
        />
      </Layout>
    </motion.div>
  );
};

